/**
 * PROC-08 Template productivity (INV-PACK-08, ENGINE-SPEC §3.3, §4, §11.4; change C-D6-PROC08).
 *
 * The pack counted distinct productive slot-frames per 10k tokens (a type-count, D6). Here every
 * anchored frame instance (a 5-gram window with ONE interior slot s ∈ {1,2,3} whose four fixed
 * words include ≥ 2 outside FRAME_STOP) looks back, censored at the document start, to the
 * previous instance of the same frame. It is *productive* when that previous instance had a
 * DIFFERENT slot filler. The channel value is the Kaplan–Meier probability
 *
 *     R_prod(ℓ),  ℓ = min(20000, largest estimable lag)
 *
 * that a frame instance recurred with a new filler within ℓ words. R_frame(ℓ) and the
 * productivity ratio R_prod/R_frame are secondary features. Gap CV is evidence only (the pack's
 * failed hypothesis). One table per slot is built and dropped in turn (≤ 48 MB each); the three
 * slot curves are pooled by adding their at-risk and event histograms.
 */
import {
	type RecurrenceCurve,
	estimableLag,
	hashWindow,
	km,
	recurrenceAt,
	scanRecurrence,
} from '../segments/recurrence.ts';
import { sdSample } from '../util/stats.ts';
import {
	abstainResult,
	derivePrior,
	languageGate,
	overdispersion,
	scoredResult,
} from './framework.ts';
import {
	SMALL,
	frameStopFlags,
	greenwoodSeAt,
	makeEvidence,
	minAtRiskFor,
	proseWordSeq,
} from './helpers.ts';
import type { ChannelDef, ChannelImpl, ChannelResult, Ramp, RunContext } from './types.ts';

// GUESS (ENGINE-SPEC §4): C2 fits this ramp on a human corpus.
const RAMP: Ramp = [
	[0, 0],
	[0.002, 0],
	[0.008, 100],
];
const L_REF = 20000;
const FRAME = 5;
const SLOTS = [1, 2, 3] as const;
const TOP_KEYS = 50;
const SHOWN = 8;
/** Sentinel filling the slot position of the hashed window (an id no token can have). */
const SLOT_MARK = 4294967000;

export const proc08Def: ChannelDef = {
	id: 'PROC-08',
	name: 'Template productivity',
	family: 'REUSE',
	axis: 'authorship',
	registers: ['prose'],
	direction: 1,
	oneWay: true,
	estimator: 'recurrence',
	features: [
		{ id: 'productive', label: 'Productive recurrence R_prod(L_ref)' },
		{ id: 'frame', label: 'Frame recurrence R_frame(L_ref)' },
		{ id: 'ratio', label: 'Productivity ratio R_prod / R_frame' },
	],
	minExposure: 8000,
	exposureUnit: 'words',
	lang: 'native',
	ramp: RAMP,
	unitScale: 1,
	unitLabel: 'productive-recurrence probability',
	prior: derivePrior(RAMP, 1, 'words', 1.5),
	weight: 0.525,
	nd: 0.15,
	changes: ['C-D6-PROC08'],
};

/** Fills `keys` and `fills` for slot s; returns the number of anchored frame instances. */
function frameKeys(
	ids: Uint32Array,
	nonStop: Uint8Array,
	W: number,
	s: number,
	keys: Float64Array,
	fills: Uint32Array,
	tmp: Float64Array
): number {
	keys.fill(0);
	let items = 0;
	for (let i = 0; i < W; i++) {
		const a =
			(nonStop[i] as number) +
			(nonStop[i + 1] as number) +
			(nonStop[i + 2] as number) +
			(nonStop[i + 3] as number) +
			(nonStop[i + 4] as number) -
			(nonStop[i + s] as number);
		if (a < 2) continue;
		for (let j = 0; j < FRAME; j++) tmp[j] = j === s ? SLOT_MARK + s : (ids[i + j] as number);
		keys[i] = hashWindow(tmp, 0, FRAME);
		fills[i] = ids[i + s] as number;
		items++;
	}
	return items;
}

function run(ctx: RunContext): ChannelResult {
	const def = proc08Def;
	const gate = languageGate(def, ctx);
	if (gate.abstain) return abstainResult(def, gate.abstain);
	const notes: string[] = [];
	if (gate.note) notes.push(gate.note);
	const { doc } = ctx;
	const seq = proseWordSeq(doc);
	const n = seq.count;
	if (n < def.minExposure)
		return abstainResult(def, `insufficient text: ${n} words < ${def.minExposure}`, notes);

	const stopById = frameStopFlags(doc);
	const nonStop = new Uint8Array(n);
	for (let i = 0; i < n; i++) nonStop[i] = stopById[seq.id[i] as number] ? 0 : 1;

	const W = n - FRAME + 1;
	const P = doc.paragraphs.count;
	const k = new Float64Array(P);
	const e = new Float64Array(P);
	const keysBySlot: Float64Array[] = [];
	const fills = new Uint32Array(n);
	const tmp = new Float64Array(FRAME);
	const top: Array<{ key: number; extra: number }> = [];
	const lmax = L_REF;
	const atRisk = new Float64Array(lmax + 2);
	const evHist = new Float64Array(lmax + 1);
	const prodHist = new Float64Array(lmax + 1);
	let items = 0;

	for (const s of SLOTS) {
		const keys = new Float64Array(n);
		items += frameKeys(seq.id, nonStop, W, s, keys, fills, tmp);
		const scan = scanRecurrence(keys, null, lmax, fills);
		const c = scan.curve;
		const pc = scan.prodCurve as RecurrenceCurve;
		for (let t = 0; t <= lmax; t++) atRisk[t] = (atRisk[t] as number) + (c.atRisk[t] as number);
		for (let t = 1; t <= lmax; t++) {
			evHist[t] = (evHist[t] as number) + (c.events[t] as number);
			prodHist[t] = (prodHist[t] as number) + (pc.events[t] as number);
		}
		const pd = scan.prodDist as Uint16Array | Uint32Array;
		for (let i = 0; i < W; i++) {
			if ((keys[i] as number) === 0) continue;
			const p = seq.para[i + FRAME - 1] as number;
			e[p] = (e[p] as number) + 1;
			if ((pd[i] as number) > 0) k[p] = (k[p] as number) + 1;
		}
		top.push(...scan.table.topByExtra(TOP_KEYS));
		keysBySlot.push(keys);
	}
	// pooled Kaplan–Meier curves: obs[t] = atRisk[t] − atRisk[t+1]
	const obs = new Float64Array(lmax + 1);
	for (let t = 0; t <= lmax; t++) obs[t] = (atRisk[t] as number) - (atRisk[t + 1] as number);
	const curveAll = km(evHist, obs, lmax, items);
	const curveProd = km(prodHist, obs, lmax, items);

	const lag = Math.min(L_REF, estimableLag(curveAll, minAtRiskFor(items)));
	const shortLookback = lag < L_REF;
	if (shortLookback) notes.push('shortLookback');
	const kappaFactor = Math.min(1, lag / L_REF);
	const Rprod = recurrenceAt(curveProd, lag);
	const Rframe = recurrenceAt(curveAll, lag);
	const ratio = Rframe > 0 ? Rprod / Rframe : 0;

	let K = 0;
	let E = 0;
	for (let p = 0; p < P; p++) {
		K += k[p] as number;
		E += e[p] as number;
	}
	const phi = overdispersion(k, e, K, E, def.prior.phi0);
	const se = greenwoodSeAt(curveProd, lag) * Math.sqrt(phi);
	const ci90: [number, number] = [Math.max(0, Rprod - 1.645 * se), Rprod + 1.645 * se];
	const kappa = (items / (items + def.prior.m)) * kappaFactor;

	// evidence: second pass restricted to the most productive frame keys
	const ev = makeEvidence();
	const summary: string[] = [];
	top.sort((a, b) => b.extra - a.extra);
	const wanted = new Set<number>();
	for (const t of top.slice(0, TOP_KEYS)) wanted.add(t.key);
	if (wanted.size > 0) {
		interface Occ {
			pos: number;
			slot: number;
		}
		const occ = new Map<number, Occ[]>();
		SLOTS.forEach((s, si) => {
			const keys = keysBySlot[si] as Float64Array;
			for (let i = 0; i < W; i++) {
				const key = keys[i] as number;
				if (key === 0 || !wanted.has(key)) continue;
				let list = occ.get(key);
				if (!list) {
					list = [];
					occ.set(key, list);
				}
				list.push({ pos: i, slot: s });
			}
		});
		const T = doc.tokens;
		const candidates: Array<{
			frame: string;
			hits: number;
			fills: number;
			gapCV: number | null;
			list: Occ[];
		}> = [];
		for (const list of occ.values()) {
			const first = list[0] as Occ;
			const words: string[] = [];
			for (let j = 0; j < FRAME; j++)
				words.push(j === first.slot ? '_' : doc.tokenText(seq.tok[first.pos + j] as number));
			const fillSet = new Set<number>();
			const gaps: number[] = [];
			for (let m = 0; m < list.length; m++) {
				const o = list[m] as Occ;
				fillSet.add(seq.id[o.pos + o.slot] as number);
				if (m > 0) gaps.push(o.pos - (list[m - 1] as Occ).pos);
			}
			let gapCV: number | null = null;
			if (gaps.length > 1) {
				const mu = gaps.reduce((a, b) => a + b, 0) / gaps.length;
				if (mu > 0) gapCV = sdSample(gaps) / mu;
			}
			candidates.push({
				frame: words.join(' '),
				hits: list.length,
				fills: fillSet.size,
				gapCV,
				list,
			});
		}
		const core = (f: string) => f.replace(/^\S+\s|\s\S+$/g, '');
		const kept: typeof candidates = [];
		candidates.sort((a, b) => b.hits - a.hits || a.frame.localeCompare(b.frame));
		for (const c of candidates) {
			if (c.hits < 3 || c.fills < 2) continue;
			const cc = core(c.frame);
			if (kept.some((q) => q.frame.includes(cc) || cc.includes(core(q.frame)))) continue;
			kept.push(c);
		}
		for (const c of kept) {
			// events: an occurrence whose previous occurrence (same frame) had a different filler, within L_ref
			for (let m = 1; m < c.list.length; m++) {
				const o = c.list[m] as Occ;
				const q = c.list[m - 1] as Occ;
				if (o.pos - q.pos > L_REF) continue;
				if ((seq.id[o.pos + o.slot] as number) === (seq.id[q.pos + q.slot] as number)) continue;
				const t = seq.tok[o.pos + o.slot] as number;
				ev.add(T.start[t] as number, (T.start[t] as number) + (T.len[t] as number), 0, 1);
			}
		}
		for (const c of kept.slice(0, SHOWN)) {
			summary.push(
				`"${c.frame}" x${c.hits} (${c.fills} fills${c.gapCV !== null ? `, gapCV ${c.gapCV.toFixed(2)}` : ''})`
			);
		}
	}

	return scoredResult(def, {
		K,
		E,
		raw: Rprod,
		estimate: Rprod,
		ci90,
		deviation: (def.direction * (Rprod - def.prior.mu)) / Math.max(se, SMALL),
		phi,
		kappa,
		perParagraph: { k, e },
		evidence: ev.build(),
		notes,
		features: { productive: Rprod, frame: Rframe, ratio, lag, items },
		summary,
	});
}

export const proc08: ChannelImpl = { def: proc08Def, phase: 'event', run };
